import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  useAddressSuggestions,
  type AddressSuggestion,
} from '@/routes/properties/useAddressSuggestions';

export interface AddressValue {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

export const FIELD_CLASS =
  'h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * The address block shared by the add- and edit-property dialogs (M1-23), with
 * autocomplete on the street line.
 *
 * The street field is a WAI-ARIA combobox over a listbox: `role="combobox"`
 * with `aria-expanded`/`aria-controls`/`aria-activedescendant` on the input,
 * `role="option"` rows, Up/Down to move, Enter to take, Escape to dismiss. A
 * `<datalist>` would have been less code and is not used on purpose: it cannot
 * carry structured values (picking fills FOUR fields, not one), its rendering
 * is the browser's and cannot be verified, and jsdom does not render it at all.
 *
 * Picking a suggestion fills line 1, city, state and ZIP in one step — the
 * AC's "keeping geocoding clean at the source". Line 2 and the unit are left
 * alone; a suggestion never knows the apartment. Typing without picking still
 * works exactly as before, and if the API says suggestions are unavailable the
 * field says so quietly underneath and nothing else changes.
 *
 * Suggestions come from `useAddressSuggestions`, which is what enforces the
 * three-character floor and the debounce; this component decides only when
 * the list is OPEN.
 */
export function AddressFields({
  value,
  onChange,
  labels,
  required = true,
  children,
}: {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  labels: { line1: string; line2: string; city: string; state: string; postalCode: string };
  required?: boolean;
  /** Rendered beside line 2 — the dialogs put the unit field there. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const listboxId = `${id}-address-listbox`;
  const optionId = (index: number) => `${id}-address-option-${index}`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { suggestions, unavailable } = useAddressSuggestions(value.line1, open);
  const expanded = open && suggestions.length > 0;

  // Announced, not just rendered: a sighted user sees the list appear; a
  // screen-reader user has to be told there is something to arrow through.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (suggestions.length > 0) {
      setAnnouncement(t('addressFields.suggestionsAvailable', { count: suggestions.length }));
    } else if (unavailable) {
      setAnnouncement(t('addressFields.unavailable'));
    }
  }, [open, suggestions.length, unavailable, t]);

  // A list that outlives its suggestions points aria-activedescendant at
  // nothing; clamp when the list shrinks under the highlight.
  useEffect(() => {
    if (activeIndex >= suggestions.length) {
      setActiveIndex(suggestions.length - 1);
    }
  }, [activeIndex, suggestions.length]);

  function select(suggestion: AddressSuggestion) {
    onChange({
      ...value,
      line1: suggestion.line1,
      city: suggestion.city,
      state: suggestion.state,
      postalCode: suggestion.postalCode,
    });
    setOpen(false);
    setActiveIndex(-1);
    setAnnouncement(t('addressFields.filledIn', { address: suggestion.formattedAddress }));
    inputRef.current?.focus();
  }

  function onLine1KeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
      }
      if (suggestions.length > 0) {
        setActiveIndex((current) => (current + 1) % suggestions.length);
      }
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (suggestions.length > 0) {
        setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
      }
      return;
    }
    if (event.key === 'Enter' && expanded && activeIndex >= 0) {
      // Taking a suggestion, not submitting the form behind it.
      event.preventDefault();
      select(suggestions[activeIndex]!);
      return;
    }
    if (event.key === 'Escape' && open) {
      // Stopped so the dialog behind does not also close on the same press.
      event.stopPropagation();
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <>
      <div className="relative">
        <label htmlFor={`${id}-line1`} className="mb-1 block text-sm font-medium">
          {labels.line1}
        </label>
        <input
          ref={inputRef}
          id={`${id}-line1`}
          required={required}
          className={FIELD_CLASS}
          value={value.line1}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={expanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-describedby={`${id}-line1-hint`}
          onChange={(event) => {
            onChange({ ...value, line1: event.target.value });
            // Open on any typing; the HOOK decides whether anything is asked
            // (the three-character floor lives there and only there, so one
            // test can prove it rather than two guards covering for each other).
            setOpen(event.target.value.trim().length > 0);
            setActiveIndex(-1);
          }}
          onKeyDown={onLine1KeyDown}
          onBlur={() => {
            setOpen(false);
            setActiveIndex(-1);
          }}
        />
        <p id={`${id}-line1-hint`} className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {unavailable && open ? t('addressFields.unavailable') : t('addressFields.hint')}
        </p>
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t('addressFields.suggestionsLabel')}
          hidden={!expanded}
          data-testid="address-suggestions"
          className="absolute left-0 right-0 z-10 mt-1 max-h-60 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-background)] py-1 text-sm shadow-md"
        >
          {suggestions.map((suggestion, index) => (
            // The keyboard path is on the input, as the combobox pattern
            // requires: options are never focused, they are pointed at with
            // aria-activedescendant and taken with Enter (tested above). A key
            // handler here would sit on an element that cannot receive keys.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={`${suggestion.formattedAddress}-${index}`}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              data-testid="address-suggestion"
              className={cn(
                'cursor-pointer px-3 py-2',
                index === activeIndex && 'bg-[var(--color-muted)]',
              )}
              // preventDefault keeps focus in the input, so blur does not close
              // the list before the click lands on the option.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => select(suggestion)}
            >
              <span className="block font-medium">{suggestion.line1}</span>
              <span className="block text-xs text-[var(--color-muted-foreground)]">
                {suggestion.city}, {suggestion.state} {suggestion.postalCode}
              </span>
            </li>
          ))}
        </ul>
        <p role="status" aria-live="polite" className="sr-only" data-testid="address-announcement">
          {announcement}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{labels.line2}</span>
          <input
            className={FIELD_CLASS}
            value={value.line2}
            onChange={(event) => onChange({ ...value, line2: event.target.value })}
          />
        </label>
        {children}
      </div>

      <div className="grid grid-cols-4 gap-3">
        <label className="col-span-2 block text-sm">
          <span className="mb-1 block font-medium">{labels.city}</span>
          <input
            required={required}
            className={FIELD_CLASS}
            value={value.city}
            onChange={(event) => onChange({ ...value, city: event.target.value })}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{labels.state}</span>
          <input
            required={required}
            className={FIELD_CLASS}
            value={value.state}
            onChange={(event) => onChange({ ...value, state: event.target.value })}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{labels.postalCode}</span>
          <input
            required={required}
            className={FIELD_CLASS}
            value={value.postalCode}
            onChange={(event) => onChange({ ...value, postalCode: event.target.value })}
          />
        </label>
      </div>
    </>
  );
}
